import type { Stripe } from '@stripe/stripe-js';
import type { CheckoutCompletionResponse } from '@/types';
import { IneligibleReasonTrialAlreadyRedeemed, IneligibleReasonTrialCardUsed } from '@/types';
import { confirmSubscriptionPayment } from '@/api/client';
import { setPendingCheckout } from '@/session';
import { logError } from '@/sdkConsole';
import { t } from '@/i18n/t';

export type TrialCompletionOutcome =
  | { kind: 'ineligible' }
  | { kind: 'completed'; completion: CheckoutCompletionResponse }
  | { kind: 'failed'; message: string };

// settleTrialCompletion resolves a confirmTrial result: a trial-gate soft
// denial (convert-to-paid handoff), success, a first invoice that needs
// on-session authentication (3DS on a setup-fee / SCA-on-create charge), or a
// still-processing subscription. Shared by the trial form's confirm and the
// flow's 3DS-return resume. loadStripe is only called when the invoice needs
// authenticating.
export async function settleTrialCompletion(completion: CheckoutCompletionResponse, loadStripe: () => Promise<Stripe | null>): Promise<TrialCompletionOutcome> {
  if (completion.ineligibleReason === IneligibleReasonTrialCardUsed || completion.ineligibleReason === IneligibleReasonTrialAlreadyRedeemed) {
    return { kind: 'ineligible' };
  }
  if (completion.status === 'completed') {
    return { kind: 'completed', completion };
  }
  if (completion.clientSecret) {
    try {
      const stripe = await loadStripe();
      if (stripe) {
        const confirmed = await confirmSubscriptionPayment(stripe, completion.checkoutId, completion.clientSecret);
        if (confirmed.status === 'completed') {
          return { kind: 'completed', completion: confirmed.completion };
        }
        if (confirmed.status === 'failed') {
          return { kind: 'failed', message: t('paymentForm.failed') };
        }
      }
    } catch (err) {
      logError('trial payment authentication failed', err);
      const detail = err instanceof Error ? err.message : t('checkout.unknownError');
      return { kind: 'failed', message: t('trialPayment.startFailedWithDetail', { detail }) };
    }
  }
  // The subscription exists but isn't provisioned yet: mark it so the next
  // load's pending-checkout resume picks up the completion.
  if (completion.checkoutId) {
    setPendingCheckout(completion.checkoutId);
  }
  return { kind: 'failed', message: t('trialPayment.stillProcessing') };
}
